import { MathsKitState, MathsKitTool, MathsKitType } from './types';
import { ObserverMap } from './utils/ObserverMap';
import EventEmitter2 from 'eventemitter2';
import { RulerTool } from './component/Ruler';
import { Triangle30Tool, Triangle45Tool } from './component/Triangle';
import { ProtractorTool } from './component/Protractor';
// import { CompassTool } from './component/Compass';
import { DEFAULT_MATHS_KIT_STATE } from './const';
import { isNumber } from 'lodash';
import { OperationManager } from './utils';
import { DrawManager } from './utils/Draw';

export interface MathsKitManagerOptions {
  /** 容器 */
  container: HTMLDivElement;
  /** 目标元素 */
  target: HTMLElement;
  /** 默认工具状态 */
  defaultKitState?: Omit<MathsKitState, 'custom' | 'zIndex' | 'type'>;
  /** 主题 */
  theme?: 'light' | 'dark';
  /** 全局缩放 */
  globalScale?: number;
}

export class MathsKitManager extends EventEmitter2 {
  private readonly _container: HTMLDivElement;
  private readonly target: HTMLElement;
  private _theme: 'light' | 'dark' = 'light';
  private _state: ObserverMap<string, MathsKitState>;
  private instances: Map<string, MathsKitTool> = new Map();
  private defaultKitState: Omit<MathsKitState, 'custom' | 'zIndex' | 'type' | 'height'>;
  private _isActive: boolean = false;
  private _strokeWidth: number = 6;
  private _globalScale: number = 1;

  private _isForwarding: boolean = false;
  private _isOperation: boolean = false;

  private operationManager: OperationManager;
  private drawManager: DrawManager;
  constructor(options: MathsKitManagerOptions) {
    super();
    this._container = options.container;
    this._theme = options.theme || 'light';
    this._globalScale = options.globalScale || 1;
    this.target = options.target;
    this.defaultKitState = options.defaultKitState || DEFAULT_MATHS_KIT_STATE;
    this._state = new ObserverMap<string, MathsKitState>();
    this.setActive(false);
    this._state.observe((operation, key, value) => {
      this.emit('stateChange', operation, key, value);
      switch (operation) {
        case 'add': {
          const instance = this.createInstance(key, value);
          if (instance) { 
            this.instances.set(key, instance);
            instance.init();
          }
          break;
        }
        case 'delete': {
          const instance = this.instances.get(key);
          if (instance) {
            instance.destroy();
          } 
          this.instances.delete(key);
          break;
        }
        case 'update': {
          const instance = this.instances.get(key);
          if (instance) {
            instance.update(value);
          } else {
            const instance = this.createInstance(key, value);
            if (instance) { 
              this.instances.set(key, instance);
              instance.init();
            }
          }
          break;
        }
      }
      this.activeManager();
    });
    // this.container.addEventListener('pointerdown', this.onDrawStartHandler, true);
    this.operationManager = new OperationManager(this.container, {
      bindEvents: ['Drag', 'Rotate', 'Stretch'],
      getGlobalScale: () => this.globalScale,
      getInstance: (id:string) => this.instances.get(id) || null,
      activeOperation: (bol:boolean)=>{this._isOperation = bol;},
      getContainerRect: () => this.container.getBoundingClientRect(),
    });
    this.drawManager = new DrawManager(this.container, {
      getGlobalScale: () => this.globalScale,
      getInstances: () => this.instances,
      activeDraw: (bol:boolean)=>{this._isForwarding = bol;},
      getForwardingTarget: () => this.target,
      getStrokeWidth: () => this.strokeWidth,
      getActiveManager: () => this.isActive,
    });
  }

  get state(): ObserverMap<string, MathsKitState> {
    return this._state;
  }

  set isOperation(isOperation: boolean) {
    this._isOperation = isOperation;
  }

  get isOperation(): boolean {
    return this._isOperation;
  }
  
  get strokeWidth(): number {
    return this._strokeWidth;
  }

  set strokeWidth(width: number) {
    this._strokeWidth = width;
  }

  get topMathsKitState(): [string, MathsKitState] | undefined {
    let top: [string, MathsKitState] | undefined;
    this._state.forEach((value, key) => {
      const zIndex = value.zIndex;
      if (!top || ( top && isNumber(zIndex) && zIndex > top[1].zIndex)) {
        top = [key, value];
      } else if (top && isNumber(zIndex) && zIndex === top[1].zIndex) {
        if (top[0] < key) {
          top = [key, value];
        }
      }
    });
    return top;
  }

  get container(): HTMLDivElement {
    return this._container;
  }

  get topZIndex(): number {
    return this.topMathsKitState && this.topMathsKitState[1].zIndex || 0;
  }

  get isActive(): boolean {
    return this._isActive;
  }

  set isActive(isActive: boolean) {
    this.setActive(isActive);
  }

  get isForwarding(): boolean {
    return this._isForwarding;
  }

  get theme(): 'light' | 'dark' {
    return this._theme;
  }

  set theme(theme: 'light' | 'dark') {
    this._theme = theme;
    this.instances.forEach(instance => {
      instance.setTheme(theme);
    });
  }

  get globalScale(): number {
    return this._globalScale;
  }

  set globalScale(scale: number) {
    let isChanged = false;
    if (this._globalScale !== scale) {
      isChanged = true;
      this._globalScale = scale;
    }
    if (isChanged) {
      this.instances.forEach(instance => {
        instance.render();
      });
    }
  }

  forwardEvent(e: Event) {
    let newEvent: Event;
    if (e instanceof PointerEvent) {
      newEvent = new PointerEvent(e.type, {
        bubbles: true,
        cancelable: true,
        composed: true,
        clientX: e.clientX,
        clientY: e.clientY,
        screenX: e.screenX,
        screenY: e.screenY,
        buttons: e.buttons,
        button: e.button,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        metaKey: e.metaKey,
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        pressure: e.pressure,
      });
    } else if (e instanceof MouseEvent) {
      newEvent = new MouseEvent(e.type, {
        bubbles: true,
        cancelable: true,
        composed: true,
        clientX: e.clientX,
        clientY: e.clientY,
        screenX: e.screenX,
        screenY: e.screenY,
        buttons: e.buttons,
        button: e.button,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        metaKey: e.metaKey,
      });
    } else {
      // fallback
      newEvent = new Event(e.type, { bubbles: true, cancelable: true });
    }
    this.target.dispatchEvent(newEvent); 
  }

  isTopMathsKit(kitId: string): boolean {
    const topMathsKitState = this.topMathsKitState;
    if (topMathsKitState && topMathsKitState[0] === kitId) {
      return true;
    }
    return false;
  }

  setActive(isActive: boolean) {
    this._isActive = isActive;
    this.activeManager();
  }

  activeManager() {
    if (this.isActive && this.instances.size > 0) {
      this.container.style.pointerEvents = 'auto';
    } else {
      this.container.style.pointerEvents = 'none';
    }
    this.instances.forEach(instance => {
      instance.setActive(this.isActive);
    });
  }

  create(type: MathsKitType, _kitId?: string, state?: Partial<MathsKitState>) {
    if ([...this._state.values()].find(s=>s.type === type)) {
      console.warn(`${type} already exists`);
      return;
    }
    const kitId = _kitId || `${type}-${Date.now()}`;

    let width = this.defaultKitState.width;
    let height = 0;
    let custom: Record<string, any> = {};
    switch (type) {
      case MathsKitType.Ruler: {
        height = 78;
        break;
      }
      case MathsKitType.Triangle45:
        width = width / Math.sqrt(2);
        height = width;
        break;
      case MathsKitType.Triangle30:
        height = width * Math.sqrt(3) / 3;
        break;
      case MathsKitType.Protractor:
        height = width / 2;
        custom = {
          startAngle: 0,
          endAngle: 30,
        };
        break;
      case MathsKitType.Compass:
        height = width;
        break;
      default:
        break;
    }

    this._state.set(kitId, {
      ...this.defaultKitState,
      width,
      height, 
      type, 
      zIndex: this.topZIndex,
      custom,
      ...state,
    });
    
  }

  update(kitId: string, state: Partial<MathsKitState>) {
    const _state = this._state.get(kitId);
    if (_state) {
      this._state.set(kitId, { ..._state, ...state });
      return;
    }
    console.warn(`${kitId} has't been created`);
    return;
  }

  remove(kitId: string) {
    this._state.delete(kitId);
    this.instances.delete(kitId);
  }

  destroy(){
    this.instances.clear();
    this.operationManager.destroy();
    this.drawManager.destroy();
    this.container.remove();
  }

  private createInstance(id: string, state: MathsKitState): MathsKitTool | null {
    const type = state.type;
    switch (type) {
      case MathsKitType.Ruler:
        return new RulerTool({
          id,
          type,
          state,
        }, this);
      case MathsKitType.Triangle45:
        return new Triangle45Tool({
          id,
          type,
          state,
        }, this);
      case MathsKitType.Triangle30:
        return new Triangle30Tool({
          id,
          type,
          state,
        }, this);
      case MathsKitType.Protractor:
        return new ProtractorTool({
          id,
          type,
          state,
        }, this);
      // case MathsKitType.Compass:
      //   return new CompassTool({
      //     id,
      //     type,
      //     state,
      //   }, this);
      default:
        return null;
    } 
  }

}